import { useTranslation } from 'react-i18next';
import { UsersRound } from 'lucide-react';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';

const ALL_DRIVERS_VALUE: string = 'all';

interface Props {
  groups: DriverGroupDto[];
  activeGroupId: string | null;
  onSelect: (groupId: string | null) => void;
}

export function DriverGroupFilter({
  groups,
  activeGroupId,
  onSelect,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();

  const handleValueChange = (value: string): void => {
    onSelect(value === ALL_DRIVERS_VALUE ? null : value);
  };

  const activeGroup: DriverGroupDto | undefined = groups.find(
    (group: DriverGroupDto): boolean => group.id === activeGroupId
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <UsersRound size={14} />
          {t('driverGroups.groups')}
          {activeGroup && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5">
              {activeGroup.name}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>{t('driverGroups.filterByGroup')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={activeGroupId ?? ALL_DRIVERS_VALUE}
          onValueChange={handleValueChange}
        >
          <DropdownMenuRadioItem value={ALL_DRIVERS_VALUE}>
            {t('driverGroups.allDrivers')}
          </DropdownMenuRadioItem>
          {groups.map((group: DriverGroupDto) => (
            <DropdownMenuRadioItem key={group.id} value={group.id}>
              {group.name}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
